import { cn } from '@/lib/utils'
import { Item } from '../../types/response'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

interface RackSlotProps {
    /** U position (1-based, bottom to top) */
    position: number
    /** Equipment occupying this slot (if any) */
    equipment?: Item
    /** Whether this slot is the top of a multi-U equipment */
    isTopOfEquipment?: boolean
    /** Total U height of the equipment (for multi-U items) */
    equipmentHeight?: number
    /** Click handler for slot */
    onClick?: (position: number, equipment?: Item) => void
    /** Whether this equipment is currently selected */
    isSelected?: boolean
}

/**
 * RackSlot - Single U position in rack layout
 * Renders as empty slot or equipment block with status coloring
 */
const RackSlot = ({
    position,
    equipment,
    isTopOfEquipment = false,
    equipmentHeight = 1,
    onClick,
    isSelected = false,
}: RackSlotProps) => {
    // Status-based colors
    const getStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'in_use':
            case 'active':
                return 'bg-green-500/80 border-green-600'
            case 'available':
                return 'bg-blue-500/80 border-blue-600'
            case 'reserved':
                return 'bg-yellow-500/80 border-yellow-600'
            case 'maintenance':
                return 'bg-orange-500/80 border-orange-600'
            case 'faulty':
            case 'decommissioned':
                return 'bg-red-500/80 border-red-600'
            default:
                return 'bg-gray-500/80 border-gray-600'
        }
    }

    const handleClick = () => {
        onClick?.(position, equipment)
    }

    // Empty slot
    if (!equipment) {
        return (
            <div
                className="h-6 flex items-center border-b border-muted-foreground/20 hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={handleClick}
            >
                <span className="w-10 text-xs text-muted-foreground text-center font-mono border-r border-muted-foreground/20">
                    U{position}
                </span>
                <div className="flex-1 h-full bg-muted/10" />
            </div>
        )
    }

    // For multi-U equipment, only render on top slot
    if (!isTopOfEquipment) {
        return null
    }

    const slotHeight = equipmentHeight * 24 // 24px per U (h-6)

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            'flex items-center cursor-pointer transition-all hover:opacity-90 hover:shadow-md',
                            'border-l-4 border-b border-b-black/20',
                            getStatusColor(equipment.status),
                            isSelected && 'ring-2 ring-primary ring-offset-1'
                        )}
                        style={{ height: `${slotHeight}px` }}
                        onClick={handleClick}
                    >
                        <span className="w-10 text-xs text-white text-center font-mono font-semibold border-r border-white/20">
                            U{position}
                        </span>
                        <div className="flex-1 h-full flex items-center px-2 overflow-hidden">
                            <span className="text-xs text-white font-medium truncate">
                                {equipment.name}
                            </span>
                        </div>
                        {equipmentHeight > 1 && (
                            <span className="text-xs text-white/70 px-2">
                                {equipmentHeight}U
                            </span>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                    <div className="space-y-1">
                        <p className="font-semibold">{equipment.name}</p>
                        <p className="text-xs text-muted-foreground">
                            {equipment.category} | {equipment.status}
                        </p>
                        {equipment.service_tag && (
                            <p className="text-xs">
                                Service Tag: {equipment.service_tag}
                            </p>
                        )}
                        {equipment.model?.name && (
                            <p className="text-xs">
                                Model: {equipment.model.name}
                            </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Position: U{position}
                            {equipmentHeight > 1 &&
                                ` - U${position - equipmentHeight + 1}`}
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

export default RackSlot
